import { useState, useRef, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Upload, Brain, CheckCircle, AlertTriangle, Loader2, Download, FileText, ClipboardCopy } from 'lucide-react';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';
import StatusBadge from '@/components/StatusBadge';

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Mapování AI struktury `extractedData` ↔ plochý editovatelný form.
function extractedToForm(ed) {
  const m = ed?.metadata || {};
  const me = ed?.measurement || {};
  return {
    name: m.name || '',
    type: m.type || '',
    manufacturer: m.manufacturer || '',
    quantity: m.quantity || '',
    rangeFrom: me.rangeFrom ?? '',
    rangeTo: me.rangeTo ?? '',
    unit: me.unit || '',
    accuracy: me.accuracy || '',
    resolution: me.resolution || '',
    readout: me.readout || '',
    notesSummary: ed?.notesSummary || '',
  };
}

function formToExtracted(form, original) {
  const toNum = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseFloat(v);
    return Number.isNaN(n) ? v : n;
  };
  return {
    ...(original || {}),
    metadata: {
      ...(original?.metadata || {}),
      name: form.name,
      type: form.type,
      manufacturer: form.manufacturer,
      quantity: form.quantity,
    },
    measurement: {
      ...(original?.measurement || {}),
      rangeFrom: toNum(form.rangeFrom),
      rangeTo: toNum(form.rangeTo),
      unit: form.unit,
      accuracy: form.accuracy,
      resolution: form.resolution,
      readout: form.readout,
    },
    notesSummary: form.notesSummary,
    confidence: original?.confidence || { overall: 1, lowConfidenceFields: [] },
  };
}

/**
 * Panel pro nahrání produktového datasheetu a AI extrakci dat.
 * Přístup B: AI navrhne → člověk zkontroluje/opraví ve formuláři → uloží.
 *
 * Props:
 *  - entityKind: 'instruments' | 'etalons'  (segment API cesty)
 *  - entityId:   id přístroje / etalonu
 *  - initialDatasheet: uložený `datasheet` sub-dokument entity (nebo undefined)
 *  - onVerified: callback po úspěšném ověření (refresh rodiče)
 *  - onPrefill:  volitelný callback(formValues) — nabídne „Předvyplnit do formuláře"
 */
export default function DatasheetPanel({ entityKind, entityId, initialDatasheet, onVerified, onPrefill }) {
  const base = `/${entityKind}/${entityId}`;
  const [datasheet, setDatasheet] = useState(initialDatasheet || null);
  const [form, setForm] = useState(() =>
    initialDatasheet?.extractedData ? extractedToForm(initialDatasheet.extractedData) : null
  );
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const fileRef = useRef(null);
  const prevId = useRef(entityId);

  // Reset jen při skutečné změně entity (ne při každém re-renderu rodiče).
  useEffect(() => {
    if (prevId.current !== entityId) {
      prevId.current = entityId;
      setDatasheet(initialDatasheet || null);
      setForm(initialDatasheet?.extractedData ? extractedToForm(initialDatasheet.extractedData) : null);
    }
  }, [entityId, initialDatasheet]);

  const ed = datasheet?.extractedData || null;
  const overall = ed?.confidence?.overall ?? null;
  const lowFields = ed?.confidence?.lowConfidenceFields || [];
  const isLow = (label, key) =>
    lowFields.some((f) => {
      const ff = String(f).toLowerCase();
      return ff.includes(key.toLowerCase()) || label.toLowerCase().includes(ff);
    }) || (overall !== null && overall < 0.8);

  const hasPdf = !!datasheet?.uploadedPdfPath;

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`${base}/upload-datasheet`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDatasheet(res.data.datasheet);
      setForm(null);
      toast.success('Datasheet nahrán — spusťte AI extrakci.');
    } catch (err) {
      toastApiError(err, 'Nepodařilo se nahrát datasheet');
    } finally {
      setUploading(false);
    }
  };

  const extract = async () => {
    setExtracting(true);
    try {
      const res = await api.post(`${base}/extract-datasheet`);
      if (res.data.error) {
        toast.error(res.data.error);
        return;
      }
      setDatasheet((prev) => ({
        ...(prev || {}),
        extractedData: res.data.extractedData,
        aiExtractionConfidence: res.data.confidence,
        verified: false,
      }));
      setForm(extractedToForm(res.data.extractedData));
      toast.success(
        `Extrakce dokončena (jistota ${Math.round((res.data.confidence || 0) * 100)} %) — zkontrolujte a uložte.`
      );
    } catch (err) {
      toastApiError(err, 'Chyba AI extrakce');
    } finally {
      setExtracting(false);
    }
  };

  const verify = async () => {
    if (!form) return;
    setVerifying(true);
    try {
      const extractedData = formToExtracted(form, datasheet?.extractedData);
      await api.put(`${base}/verify-datasheet`, { extractedData });
      setDatasheet((prev) => ({ ...(prev || {}), extractedData, verified: true }));
      toast.success('Datasheet ověřen a uložen');
      onVerified?.();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se uložit datasheet');
    } finally {
      setVerifying(false);
    }
  };

  const download = async () => {
    try {
      const token = localStorage.getItem('ct-token');
      const resp = await fetch(`${BACKEND_BASE}/api${base}/datasheet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = datasheet?.originalFilename || 'datasheet.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Nepodařilo se stáhnout datasheet');
    }
  };

  const field = (label, key, { type = 'text', textarea = false, colSpan = false } = {}) => (
    <div className={`ct-form-field ${colSpan ? 'col-span-2' : ''} ${isLow(label, key) ? 'ring-1 ring-yellow-500/50 rounded-md p-2' : ''}`}>
      <label className="ct-form-label flex items-center gap-1">
        {label}
        {isLow(label, key) && <AlertTriangle className="w-3 h-3" style={{ color: 'var(--ct-warning)' }} />}
      </label>
      {textarea ? (
        <Textarea
          value={form[key] ?? ''}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          rows={2}
          className="text-sm"
        />
      ) : (
        <Input
          type={type}
          value={form[key] ?? ''}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          className="h-9 text-sm"
        />
      )}
    </div>
  );

  const envConditions = ed?.environmentalConditions;

  return (
    <div className="ct-panel">
      <div className="ct-panel-header flex items-center gap-2">
        <FileText className="w-4 h-4" />
        Datasheet (AI extrakce)
        {datasheet?.verified && (
          <span className="ml-1 flex items-center gap-1 text-xs" style={{ color: 'var(--ct-success)' }}>
            <CheckCircle className="w-3.5 h-3.5" /> Ověřeno
          </span>
        )}
      </div>
      <div className="p-4 space-y-3">
        <p className="text-xs" style={{ color: 'var(--ct-text-disabled)' }}>
          Nahrajte produktový datasheet (PDF). AI z něj navrhne metrologické a identifikační údaje;
          před uložením je zkontrolujte a případně opravte. Postup kalibrace se z datasheetu nepřebírá —
          ten patří do metodiky.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="application/pdf" onChange={uploadPdf} className="hidden" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="ct-btn-secondary gap-1">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {hasPdf ? 'Nahradit PDF' : 'Nahrát datasheet (PDF)'}
          </Button>
          {hasPdf && (
            <Button onClick={download} className="ct-btn-secondary gap-1" title="Stáhnout nahraný datasheet">
              <Download className="w-4 h-4" /> {datasheet.originalFilename || 'PDF'}
            </Button>
          )}
          <Button
            data-testid="extract-datasheet-btn"
            onClick={extract}
            disabled={!hasPdf || extracting}
            className="ct-btn-primary gap-1"
          >
            {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
            {extracting ? 'Extrahuji…' : 'Spustit AI extrakci'}
          </Button>
        </div>

        {form && (
          <div className="mt-2 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--ct-accent)' }}>
                Extrahovaná data — zkontrolujte a opravte
              </h3>
              {overall !== null && <StatusBadge tone="info">Jistota: {Math.round(overall * 100)} %</StatusBadge>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {field('Název / model', 'name')}
              {field('Typ / model. ozn.', 'type')}
              {field('Výrobce', 'manufacturer')}
              {field('Veličina', 'quantity')}
              {field('Rozsah od', 'rangeFrom', { type: 'number' })}
              {field('Rozsah do', 'rangeTo', { type: 'number' })}
              {field('Jednotka', 'unit')}
              {field('Odečet (digital/analog)', 'readout')}
              {field('Přesnost / třída (MPE)', 'accuracy', { colSpan: true })}
              {field('Rozlišení', 'resolution', { colSpan: true })}
              {field('Shrnutí dalších údajů', 'notesSummary', { textarea: true, colSpan: true })}
            </div>

            {envConditions && (
              <div className="ct-form-field">
                <label className="ct-form-label">Okolní / referenční podmínky (z datasheetu)</label>
                <div
                  className="rounded p-2 text-xs font-mono break-words"
                  style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text-secondary)' }}
                >
                  {JSON.stringify(envConditions, null, 2)}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button data-testid="verify-datasheet-btn" onClick={verify} disabled={verifying} className="ct-btn-primary gap-1">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Ověřit a uložit
              </Button>
              {onPrefill && (
                <Button onClick={() => onPrefill(form)} className="ct-btn-secondary gap-1" title="Zkopíruje hodnoty do formuláře nahoře (neukládá)">
                  <ClipboardCopy className="w-4 h-4" /> Předvyplnit do formuláře
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
